/**
 * Editor для ref.price_groups (Порція Роздріб.5b).
 * Простий довідник: save / delete. Без posted/unpost.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { retailPriceGroups } from '@/api/retailPriceGroups';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useNextRefCode } from '@/hooks/useNextRefCode';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequirePermission from '@/auth/RequirePermission';
import RequiredLabel from '@/components/RequiredLabel';

const PERMISSION = 'retail.price_groups.manage';

interface FormState {
  organization_id: string;
  code: string;
  name: string;
  description: string;
  is_default: boolean;
  is_active: boolean;
  sort_order: string;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  code: '',
  name: '',
  description: '',
  is_default: false,
  is_active: true,
  sort_order: '100',
  comment: '',
};

export default function PriceGroupEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg(PERMISSION);

  const docQuery = useQuery({
    queryKey: ['retail-price-group', id],
    queryFn: () => retailPriceGroups.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  // Авто-пропозиція коду <PG-NN>
  const nextCode = useNextRefCode({
    queryKey: ['retail-price-groups', form.organization_id, 'for-next-code'],
    queryFn: () =>
      retailPriceGroups.list({ organization_id: form.organization_id, limit: 500 }),
    extractCodes: (data) => data.items.map((i) => i.code),
    prefix: 'PG',
    enabled: isNew && !!form.organization_id,
  });

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  // Авто-підставляємо запропонований код, поки касир його не змінив.
  useEffect(() => {
    if (isNew && nextCode.suggested && !form.code) {
      setForm((f) => ({ ...f, code: nextCode.suggested }));
    }
  }, [isNew, nextCode.suggested, form.code]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        code: d.code,
        name: d.name,
        description: d.description ?? '',
        is_default: d.is_default,
        is_active: d.is_active,
        sort_order: String(d.sort_order ?? 100),
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const create = useMutation({
    mutationFn: () =>
      retailPriceGroups.create({
        organization_id: form.organization_id,
        code: form.code,
        name: form.name,
        description: form.description || null,
        is_default: form.is_default,
        is_active: form.is_active,
        sort_order: form.sort_order ? Number(form.sort_order) : null,
        comment: form.comment || null,
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-price-groups'] });
      navigate(`/retail/price-groups/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const update = useMutation({
    mutationFn: () =>
      retailPriceGroups.update(id!, {
        code: form.code,
        name: form.name,
        description: form.description || null,
        is_default: form.is_default,
        is_active: form.is_active,
        sort_order: form.sort_order ? Number(form.sort_order) : null,
        comment: form.comment || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-price-groups'] });
      qc.invalidateQueries({ queryKey: ['retail-price-group', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const remove = useMutation({
    mutationFn: () => retailPriceGroups.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-price-groups'] });
      navigate('/retail/price-groups');
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const orgName = (oid: string) =>
    orgList.data?.items.find((o) => o.id === oid)?.name ?? oid;

  const handleSave = () => {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        code: form.code,
        name: form.name,
      },
      { organization_id: 'Юрособа', code: 'Код', name: 'Назва' },
    );
    if (err) {
      setError(err);
      return;
    }
    if (isNew) create.mutate();
    else update.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова цінова група' : `Цінова група: ${form.code} — ${form.name}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/retail/price-groups')}>Скасувати</button>
          <RequirePermission code={PERMISSION}>
            <button
              className="primary"
              onClick={handleSave}
              disabled={create.isPending || update.isPending}
            >
              Зберегти
            </button>
          </RequirePermission>
          {!isNew && (
            <RequirePermission code={PERMISSION}>
              <button
                className="danger"
                onClick={() => {
                  if (window.confirm('Видалити цінову групу?')) remove.mutate();
                }}
              >
                Видалити
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <RequiredLabel>Організація</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.code} — {o.name}
                </option>
              ))}
            </select>
          ) : (
            <div>{orgName(form.organization_id)}</div>
          )}
        </div>
        <div className="field">
          <RequiredLabel>Код</RequiredLabel>
          <input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            maxLength={30}
          />
        </div>
        <div className="field">
          <RequiredLabel>Назва</RequiredLabel>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            maxLength={255}
          />
        </div>
        <div className="field">
          <label>Порядок сортування</label>
          <input
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Опис</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
          />
        </div>
        <div className="field">
          <label>
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
            />
            {' '}Група за замовчуванням
          </label>
        </div>
        <div className="field">
          <label>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            {' '}Активна
          </label>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <textarea
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            rows={2}
          />
        </div>
      </div>

      {error && <div className="error" style={{ marginTop: '1rem', color: '#b91c1c' }}>{error}</div>}
    </div>
  );
}
